/**
 * The organisation the screens are reading right now: the session's own, or
 * the book flipped to in the header (8 Sep 2026). Everything that used to ask
 * "is this the wholesale book?" of the session asks it here instead, so a
 * head-office login standing in the Autozone book gets the Autozone rail,
 * home and buttons.
 */
import { useEffect } from 'react';
import { useAuth } from './auth';
import { BOOK_ORG, BOOK_WHOLESALE, booksFor, useActiveBook, type BookKey } from './book';

export interface ActiveOrg {
  id: string | null;
  wholesale: boolean;
  /** The book key when the session is reading a book other than its own. */
  book: BookKey | null;
}

export function useActiveOrg(): ActiveOrg {
  const { user } = useAuth();
  const active = useActiveBook();
  const own = user?.organization?.id ?? user?.organizationId ?? null;
  const allowed = booksFor(user);
  if (active && allowed.includes(active) && BOOK_ORG[active] !== own) {
    return { id: BOOK_ORG[active], wholesale: BOOK_WHOLESALE[active], book: active };
  }
  return { id: own, wholesale: Boolean(user?.organization?.wholesale), book: null };
}

/**
 * Put the active book's identity on <html>, beside `data-theme`.
 *
 * The maker's book wears its own palette (owner, 19 Sep 2026: *"different
 * colour scheme from other books"*), and tokens.css carries it as an override
 * scoped to `[data-book='maker']`. It has to live on the document element, not
 * on a wrapper, for two reasons: `readChartColor` resolves every chart colour
 * off `documentElement`, so a wrapper would leave the charts in retail blue on
 * a violet page; and the page background is painted by `body`, which no
 * wrapper contains.
 *
 * Retail books set nothing at all rather than `data-book="retail"` — the base
 * `:root` already IS the retail identity, and a second name for the default is
 * a second thing to keep in step.
 */
export function useBookIdentity(): void {
  const { wholesale } = useActiveOrg();
  useEffect(() => {
    const root = document.documentElement;
    if (wholesale) root.setAttribute('data-book', 'maker');
    else root.removeAttribute('data-book');
    // Signing out, or switching to a partner's book, must take the identity
    // with it — otherwise the retail book keeps the maker's page colour.
    return () => root.removeAttribute('data-book');
  }, [wholesale]);
}
